'use client';

import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface ProbeDataItem {
  insectCollection?: {
    beetle?: number | null;
    moth?: number | null;
    weevil?: number | null;
    other?: number | null;
  } | null;
  particleCount: number;
  createdAt: string;
  temperature?: string;
  humidity?: string;
}

export interface InsectCountChartProps {
  data: ProbeDataItem[];
  probeId: string;
  probeName?: string;
}

// Data filter options
type DataFilter = 'past7points' | 'past30points' | 'fullHistory';

interface DataFilterOption {
  value: DataFilter;
  label: string;
  description: string;
}

// These are now dynamically generated using translations in the component

// Chart type options
type ChartType = 'insectCount' | 'temperature' | 'humidity';

interface ChartTypeOption {
  value: ChartType;
  label: string;
  description: string;
}

// These are now dynamically generated using translations in the component

export default function InsectCountChart({ data, probeId, probeName }: InsectCountChartProps) {
  const chartRef = useRef<ChartJS<'line'>>(null);
  const [dataFilter, setDataFilter] = useState<DataFilter>('past7points');
  const [chartType, setChartType] = useState<ChartType>('insectCount');
  const { t, i18n } = useTranslation(['dashboard', 'common']);
  
  // Get fahrenheit preference from Redux store
  const { userInfo } = useSelector((state: RootState) => state.data);
  const fahrenheit = userInfo?.fahrenheit || false;

  // Get current locale for date formatting
  const getDateLocale = () => {
    return i18n.language === 'zh' ? 'zh-CN' : 'en-US';
  };

  // Generate data filter options with translations
  const dataFilterOptions: DataFilterOption[] = [
    { value: 'past7points', label: t('dashboard:past7PointsLabel'), description: t('dashboard:showLast7') },
    { value: 'past30points', label: t('dashboard:past30PointsLabel'), description: t('dashboard:showLast30') },
    { value: 'fullHistory', label: t('dashboard:fullHistoryLabel'), description: t('dashboard:showAllData') }
  ];

  // Generate chart type options with translations
  const chartTypeOptions: ChartTypeOption[] = [
    { value: 'insectCount', label: t('dashboard:insectCountChart'), description: t('dashboard:showInsectCount') },
    { value: 'temperature', label: t('dashboard:temperatureChart'), description: t('dashboard:showTemperature') },
    { value: 'humidity', label: t('dashboard:humidityChart'), description: t('dashboard:showHumidity') }
  ];

  // Format probe display name
  const getProbeDisplayName = () => {
    if (probeName && probeName !== 'None' && probeName.trim() !== '') {
      return `${probeName} (${probeId})`;
    }
    return probeId;
  };

  // Filter data based on selected data point count
  const filterDataByPoints = (data: ProbeDataItem[]): ProbeDataItem[] => {
    if (dataFilter === 'fullHistory') {
      return data;
    }

    // Sort data by date (newest first) to get the most recent data points
    const sortedData = [...data].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    let pointsToShow = 0;
    if (dataFilter === 'past7points') {
      pointsToShow = 7;
    } else if (dataFilter === 'past30points') {
      pointsToShow = 30;
    }

    // Take the specified number of most recent data points
    return sortedData.slice(0, pointsToShow);
  };

  // Process data for the chart based on chart type
  const processChartData = (): ChartData<'line'> => {
    if (!data || data.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    // Filter data first, then sort by date (oldest first for chart display)
    const filteredData = filterDataByPoints(data);
    const sortedData = [...filteredData].sort((a, b) => 
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );

    // Format labels (dates)
    const labels = sortedData.map(item => {
      const date = new Date(item.createdAt);
      return date.toLocaleDateString(getDateLocale(), {
        month: 'short',
        day: 'numeric',
        year: '2-digit'
      });
    });

    if (chartType === 'insectCount') {
      // Calculate total insect count for each data point
      const totalCounts = sortedData.map(item => {
        if (item.insectCollection) {
          const moth = item.insectCollection.moth || 0;
          const beetle = item.insectCollection.beetle || 0;
          const weevil = item.insectCollection.weevil || 0;
          const other = item.insectCollection.other || 0;
          return moth + beetle + weevil + other;
        }
        return item.particleCount || 0;
      });

      // Individual insect type data
      const mothCounts = sortedData.map(item => item.insectCollection?.moth || 0);
      const beetleCounts = sortedData.map(item => item.insectCollection?.beetle || 0);
      const weevilCounts = sortedData.map(item => item.insectCollection?.weevil || 0);
      const otherCounts = sortedData.map(item => item.insectCollection?.other || 0);

      return {
        labels,
        datasets: [
          {
            label: t('dashboard:totalInsects'),
            data: totalCounts,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 3,
            pointRadius: 5,
            pointHoverRadius: 7,
            tension: 0.1,
          },
          {
            label: t('dashboard:moths'),
            data: mothCounts,
            borderColor: 'rgb(236, 72, 153)',
            backgroundColor: 'rgba(236, 72, 153, 0.1)',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.1,
          },
          {
            label: t('dashboard:beetles'),
            data: beetleCounts,
            borderColor: 'rgb(34, 197, 94)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.1,
          },
          {
            label: t('dashboard:weevils'),
            data: weevilCounts,
            borderColor: 'rgb(249, 115, 22)',
            backgroundColor: 'rgba(249, 115, 22, 0.1)',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.1,
          },
          {
            label: t('dashboard:other'),
            data: otherCounts,
            borderColor: 'rgb(168, 85, 247)',
            backgroundColor: 'rgba(168, 85, 247, 0.1)',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            tension: 0.1,
          }
        ]
      };
    } else if (chartType === 'temperature') {
      // Temperature data with Fahrenheit conversion if needed
      const temperatureData = sortedData.map(item => {
        const tempCelsius = parseFloat(item.temperature || '0');
        if (isNaN(tempCelsius)) return 0;
        const convertedTemp = fahrenheit ? (tempCelsius * 9/5) + 32 : tempCelsius;
        return Math.round(convertedTemp);
      });

      return {
        labels,
        datasets: [
          {
            label: `${t('dashboard:temperature')} (°${fahrenheit ? 'F' : 'C'})`,
            data: temperatureData,
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderWidth: 3,
            pointRadius: 5,
            pointHoverRadius: 7,
            tension: 0.1,
          }
        ]
      };
    } else if (chartType === 'humidity') {
      // Humidity data
      const humidityData = sortedData.map(item => {
        const humidity = parseFloat(item.humidity || '0');
        return isNaN(humidity) ? 0 : humidity;
      });

      return {
        labels,
        datasets: [
          {
            label: `${t('dashboard:relativeHumidity')} (%)`,
            data: humidityData,
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 3,
            pointRadius: 5,
            pointHoverRadius: 7,
            tension: 0.1,
          }
        ]
      };
    }

    return { labels: [], datasets: [] };
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: `${chartTypeOptions.find(option => option.value === chartType)?.label} ${t('dashboard:overTime')} - SmartProbe ${getProbeDisplayName()}`,
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          bottom: 20
        }
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: (context) => {
            if (context[0]) {
              const dataIndex = context[0].dataIndex;
              const originalDate = new Date(data[dataIndex]?.createdAt);
              return originalDate.toLocaleDateString(getDateLocale(), {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              });
            }
            return '';
          },
          label: (context) => {
            const unit = chartType === 'insectCount' ? ` ${t('dashboard:insects')}` : 
                        chartType === 'temperature' ? `°${fahrenheit ? 'F' : 'C'}` : 
                        '%';
            return `${context.dataset.label}: ${context.parsed.y}${unit}`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: t('dashboard:date'),
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          maxRotation: 45,
          font: {
            size: 11
          }
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: chartType === 'insectCount' ? t('dashboard:insectCount') : 
                chartType === 'temperature' ? `${t('dashboard:temperature')} (°${fahrenheit ? 'F' : 'C'})` : 
                `${t('dashboard:relativeHumidity')} (%)`,
          font: {
            size: 14,
            weight: 'bold'
          }
        },
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          stepSize: 1,
          font: {
            size: 11
          }
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    },
    elements: {
      point: {
        hoverBackgroundColor: 'white',
        hoverBorderWidth: 2
      }
    }
  };

  const chartData = processChartData();
  const filteredData = filterDataByPoints(data || []);

  // Show empty state if no data at all
  if (!data || data.length === 0) {
    return (
      <div>
        {/* Chart Controls */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">{chartTypeOptions.find(option => option.value === chartType)?.label} {t('dashboard:overTime')}</h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label htmlFor="chart-type" className="text-sm font-medium text-gray-900">
                  {t('dashboard:chartTypeLabel')}
                </label>
                <select
                  id="chart-type"
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value as ChartType)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                  disabled
                >
                  {chartTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <label htmlFor="data-filter" className="text-sm font-medium text-gray-900">
                  {t('dashboard:dataPointsLabel')}
                </label>
                <select
                  id="data-filter"
                  value={dataFilter}
                  onChange={(e) => setDataFilter(e.target.value as DataFilter)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                  disabled
                >
                  {dataFilterOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center h-64 text-gray-800">
          <div className="text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-lg font-medium">{t('dashboard:noDataAvailable')}</p>
            <p className="text-sm">{chartTypeOptions.find(option => option.value === chartType)?.label.toLowerCase()} {t('dashboard:willAppearHere')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no data for the selected time period
  if (filteredData.length === 0) {
    return (
      <div>
        {/* Chart Controls */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">{chartTypeOptions.find(option => option.value === chartType)?.label} {t('dashboard:overTime')}</h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label htmlFor="chart-type" className="text-sm font-medium text-gray-900">
                  {t('dashboard:chartTypeLabel')}
                </label>
                <select
                  id="chart-type"
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value as ChartType)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                >
                  {chartTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <label htmlFor="data-filter" className="text-sm font-medium text-gray-900">
                  {t('dashboard:dataPointsLabel')}
                </label>
                <select
                  id="data-filter"
                  value={dataFilter}
                  onChange={(e) => setDataFilter(e.target.value as DataFilter)}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                >
                  {dataFilterOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center h-64 text-gray-800">
          <div className="text-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-lg font-medium">{t('dashboard:noDataForPeriod')} {dataFilterOptions.find(option => option.value === dataFilter)?.label}</p>
            <p className="text-sm">{t('dashboard:tryDifferentRange')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Chart Controls */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">{chartTypeOptions.find(option => option.value === chartType)?.label} {t('dashboard:overTime')}</h3>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <label htmlFor="chart-type" className="text-sm font-medium text-gray-900">
                {t('dashboard:chartTypeLabel')}
              </label>
              <select
                id="chart-type"
                value={chartType}
                onChange={(e) => setChartType(e.target.value as ChartType)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
              >
                {chartTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <label htmlFor="data-filter" className="text-sm font-medium text-gray-900">
                {t('dashboard:dataPointsLabel')}
              </label>
              <select
                id="data-filter"
                value={dataFilter}
                onChange={(e) => setDataFilter(e.target.value as DataFilter)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
              >
                {dataFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      
      <div className="h-96">
        <Line ref={chartRef} data={chartData} options={chartOptions} />
      </div>
      
      {/* Chart Statistics */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <p className="text-gray-800">{t('dashboard:totalDataPoints')}</p>
            <p className="font-semibold text-lg">{filteredData.length}</p>
          </div>
          <div className="text-center">
            <p className="text-gray-800">{t('dashboard:dateRange')}</p>
            <p className="font-semibold text-sm">
              {filteredData.length > 0 && (
                <>
                  {new Date(Math.min(...filteredData.map(d => new Date(d.createdAt).getTime()))).toLocaleDateString(getDateLocale(), { month: 'short', day: 'numeric' })}
                  {' - '}
                  {new Date(Math.max(...filteredData.map(d => new Date(d.createdAt).getTime()))).toLocaleDateString(getDateLocale(), { month: 'short', day: 'numeric' })}
                </>
              )}
            </p>
          </div>
          <div className="text-center">
            <p className="text-gray-800">
              {chartType === 'insectCount' ? t('dashboard:maxCount') : t('dashboard:average')}
            </p>
            <p className="font-semibold text-lg">
              {chartData.datasets[0] && chartData.datasets[0].data.length > 0 ? 
                chartType === 'insectCount' ? 
                  Math.max(...chartData.datasets[0].data as number[]) :
                  Math.round((chartData.datasets[0].data as number[]).reduce((a, b) => a + b, 0) / (chartData.datasets[0].data as number[]).length)
                : 0}
              {chartType === 'temperature' ? `°${fahrenheit ? 'F' : 'C'}` : chartType === 'humidity' ? '%' : ''}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

